import checkoutUtils from '../containers/checkout/checkoutUtils';

/* Replaces the prototype's computeTotals. Same shape out — the summary, the
   drawer and the receipt all read { lines, subtotal, ... } — but every number
   now comes from the server's subCosts by way of calculateOrderDerivedValues,
   which already handles actualCostAsShipping, gift-card and points discounts,
   shipping insurance and the free-shipping threshold.

   Nothing here computes money. If this file ever multiplies a price by a
   quantity, the mock pricing engine has grown back: amountToCharge is the
   server's answer for a line, and subCosts is its answer for the order. */

/* Matched on both sides: checkoutUtils.js:102-104 adds it to shipping here, and
   cart.lib.js:910 charges it there. */
export const DISCREET_SHIPPING = 4.99;

const asNumber = (value) => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/* Media is a flat list across colors and variations, so a product with options
   carries several entries and only one of them is the item in the cart. */
const imageFor = (product, { color, variation }) => {
  const media = product?.media || [];
  const match = media.find(
    (item) =>
      (!color || item.color === color) &&
      (!variation || item.variation === variation),
  );
  return (match || media[0])?.url;
};

/* Whether the coupon discounts the plan itself. Exported because two places
   have to agree on it: the line price below is taken from the coupon when it
   does, so anything asking "is this money off already shown" asks here. */
export const couponAppliesToPlan = (cartInfo) => {
  const planId = cartInfo?.subscription?.plan?._id;
  if (!planId) return false;
  return (cartInfo.coupon?.exclusivePlans || []).some(
    (plan) => plan._id === planId,
  );
};

/* What the shopper pays for the plan this order, which is not plan.actualCost
   whenever a coupon applies to it. The rule is the review page's, at
   components/review/index.js:477-499 — the coupon carries either a finalCost or
   a discountPercentage, and a plan billed as shipping costs nothing as a line.

   This is the one place here that touches arithmetic, and only because the
   server sends the coupon rather than the discounted plan price. The order is
   still the server's: subCosts.couponDiscount is what comes off the total. */
/* subscriptionPrice is the purchase's answer: a receipt is priced from what was
   charged, and a purchase record carries plan.costWithCoupon rather than the
   coupon fields the rules below read. */
const subscriptionCostFor = (cartInfo, subscriptionPrice) => {
  if (subscriptionPrice != null) return asNumber(subscriptionPrice);

  const { plan } = cartInfo.subscription;
  if (plan.actualCostAsShipping) return 0;

  let cost = asNumber(plan.actualCost);
  if (!couponAppliesToPlan(cartInfo)) return cost;

  if (cartInfo.coupon.finalCost != null) return asNumber(cartInfo.coupon.finalCost);
  cost -= cost * (asNumber(cartInfo.coupon.discountPercentage) * 0.01);
  return cost;
};

/* A prepaid plan buys every box up front. There is no first box to price
   separately, which is what both rules below turn on. */
const isPrepaid = (cartInfo) =>
  !!cartInfo?.subscription?.plan &&
  cartInfo.subscription.plan.months !== 'INFINITY';

/* How often the plan bills, which is the one thing about it a shopper cannot
   read off the price. Only a month-to-month plan bills again — a prepaid one is
   paid for here and now, so it says nothing. The wording and the split by plan
   type are legacy's, at components/cart-total/index.js:74-86 and
   components/review/index.js:528-531. */
const subscriptionCadence = (plan) => {
  if (plan.months !== 'INFINITY') return null;
  return plan.type === 'BOX' ? 'Billed monthly' : 'Billed every 21 days';
};

const subscriptionLine = (cartInfo, subscriptionPrice) => {
  const { plan } = cartInfo.subscription;
  const price = subscriptionCostFor(cartInfo, subscriptionPrice);
  /* Struck against list where there is one, and against the plan's own price
     where a coupon took it lower — whichever is the larger claim the shopper
     can check. */
  const wasPrice = Math.max(
    asNumber(plan.originalCost),
    asNumber(plan.actualCost),
  );

  /* A rolling plan renews; a numbered one is paid for here and now. What earns
     the PDP's "first month, then" framing is not the commitment but the gap
     between the two charges: quoting a second price that equals the first says
     something changes when nothing does. So the framing turns on the discount,
     and a plan at list price just says what it costs per month.

     Only for boxes. A vitamin plan bills every 21 days, so "/mo" would be a
     false claim and "first month" the wrong unit — those keep the cadence note
     the price column cannot carry. */
  const isRolling = plan.months === 'INFINITY';
  const isBox = plan.type === 'BOX';
  /* actualCost is what the plan charges every cycle; a cart coupon can take the
     first charge below it, and originalCost is the list price the plan itself is
     discounted from. So there are two different discounts here, and they are
     not the same claim: a coupon takes money off the *first* box, while a plan
     under list is cheaper every box. */
  const recurring = asNumber(plan.actualCost);
  const listPrice = asNumber(plan.originalCost);
  const firstChargeIsLower = price < recurring;
  const renews = isRolling && isBox && firstChargeIsLower;
  const percentOff = (from, to) => Math.round((1 - to / from) * 100);

  return {
    id: plan._id,
    name: `${plan.title} Subscription`,
    /* Only the cadence the price column cannot say. A monthly box reads it off
       "/month" or "then $x/mo", so "Billed monthly" was saying it twice; a
       21-day cycle has no such column and keeps the note. Wording is legacy's
       (components/cart-total/index.js:74-86). */
    note: isRolling && !isBox ? subscriptionCadence(plan) : null,
    /* Name the discount that is actually being given. "Off first box" is only
       true when the first charge is lower than the ones after it; a plan under
       list is cheaper every box, and saying "first box" there would undersell
       it and mislead. A prepaid term gets neither: the discount is off the
       whole term, and the struck price already says so. */
    promoLabel: (() => {
      if (isPrepaid(cartInfo)) return null;
      if (firstChargeIsLower) {
        return `${percentOff(recurring, price)}% off first box`;
      }
      if (listPrice > recurring) return `${percentOff(listPrice, recurring)}% off`;
      return null;
    })(),
    /* The coupon's own gift options, the same list the picker reads. The PDP
       shows this badge for any discounted plan; here it is shown only when the
       cart really has a gift to give. */
    freeGift: (cartInfo?.coupon?.addedProductsOptions || []).length > 0,
    // What renews, and only when it differs from what is charged now.
    recurringPrice: renews ? recurring : null,
    // A monthly box at list price: one number, said once.
    pricePer: isRolling && isBox && !renews ? '/month' : null,
    wasPrice: wasPrice > price ? wasPrice : null,
    price,
    image: plan.imageUrl || plan.image,
    recurring: true,
    kind: 'sub',
  };
};

/* A recurring add-on prices its first month separately, and the cart does not
   carry that price: ownPromoData.firstMonthCost is on the product record, not
   on the cart line, so amountToCharge is the ongoing price and shows the
   premium upgrade at $12.99 in the month it is free.

   Start Membership has the same gap and closes it the same way — it fetches the
   product itself and displays firstMonthCost off that (YourCart.js:269), rather
   than asking the cart for it. The caller passes what it fetched in here, keyed
   by product id. */
export const GIFT_BAG_TAG = 'Checkout Gift Options';

const isGiftBag = (product) =>
  (product?.tags || []).some((tag) => tag?.description === GIFT_BAG_TAG);

/* Two flags cover three things. isBundledGWPProduct is the take-everything
   bundle, but a GWP the shopper picked arrives as isAddedCouponProduct like any
   other coupon gift — the only thing that tells them apart is whether the
   product is one of the coupon's own addedProductsOptions. That is the review
   page's test, at components/review/index.js:62-83.

   The gift bag is an ordinary paid product, promoItem false, as legacy adds it
   (checkout/index.js:1501) — the server does not pin its quantity, so its tag
   is the only thing saying one per order. Not 'promo': that means server-pinned. */
const lineKind = (entry, gwpOptionIds) => {
  if (entry.isBundledGWPProduct) return 'gwp';
  if (entry.isAddedCouponProduct) {
    return gwpOptionIds.has(entry.product?._id) ? 'gwp' : 'gift';
  }
  if (entry.promoItem) return 'promo';
  if (isGiftBag(entry.product)) return 'giftBag';
  return 'product';
};

/* The server's own test, verbatim (cart.lib.js:359-369): a product by this
   name on a prepaid plan is charged months × ownPromoData.cost, and the
   firstMonthCost override sits in the branch after it, so it is never reached.
   The subscription's own copy of the add-on is set to 0 for the same case
   (cart.utils.js:341-350) — the term is paid for here, not given away.

   Matched by name because that is what the server matches on. Two premium
   upgrade products exist (services/premiumUpgrade.js), and only one of them
   answers to this name; the other keeps its free first month on any plan. */
const isBundledPremiumUpgrade = (product, prepaid) =>
  prepaid && product.name === 'Premium Upgrade';

const productLine = (entry, firstMonthPrices, gwpOptionIds, prepaid, index) => {
  const { product, quantity, color, variation } = entry;

  const firstMonthCost = firstMonthPrices[product._id];
  const hasFirstMonthPrice =
    firstMonthCost != null && !isBundledPremiumUpgrade(product, prepaid);

  const price = hasFirstMonthPrice
    ? asNumber(firstMonthCost)
    : asNumber(entry.amountToCharge);
  /* Struck against what it costs from next month on — which the cart drawer
     renders as a strike (CartDrawer.jsx:77) and the order summary renders as
     "first month, then $x/mo", the same claim the subscription line makes. Both
     read the same line, so it carries both and each picks the one it draws. */
  const wasPrice = hasFirstMonthPrice
    ? asNumber(entry.amountToCharge)
    : asNumber(product.originalCost) * (quantity || 1);
  const recurringPrice = hasFirstMonthPrice ? wasPrice : null;

  return {
    id: entry._id,
    /* A temp cart's entries have no _id — addItemToTempCart pushes what it was
       given (BitsyApollo.js:438) — so id is undefined on every line until the
       shopper has a token. Position is the only identity that holds either
       way, and it is what CartUtils' own temp-cart branches splice on. */
    entryIndex: index,
    name: product.name,
    /* A coupon's free gift and a bundled GWP are both gifts, but they are not
       the same gift: the GWP is the one the shopper picks at checkout, so the
       cart drawer leaves it to that screen. A promo item is a fixed one-off —
       addProductToCart pins its quantity to 1 — so it is not the shopper's to
       count either, and a gift bag is one per order by the same logic. Only a
       plain product draws quantity controls. */
    kind: lineKind(entry, gwpOptionIds),
    wasPrice: wasPrice > price ? wasPrice : null,
    // Only when it differs from what is charged now, as the plan line does it.
    recurringPrice: recurringPrice > price ? recurringPrice : null,
    price,
    image: imageFor(product, { color, variation }),
    qty: quantity || 1,
  };
};

export const buildLineItems = (
  cartInfo,
  { firstMonthPrices = {}, subscriptionPrice } = {},
) => {
  if (!cartInfo) return [];

  const gwpOptionIds = new Set(
    (cartInfo.coupon?.addedProductsOptions || []).map((option) => option._id),
  );

  const prepaid = isPrepaid(cartInfo);

  const lines = [];
  if (cartInfo.subscription?.plan) {
    lines.push(subscriptionLine(cartInfo, subscriptionPrice));
  }

  /* The column reads as what the shopper is signing up for: the plan, then what
     was added to it, then the gift that came with it. The upgrade is accepted
     after the coupon has already put the gift in the cart, so entry order alone
     leaves it underneath — isRecurringAddOn is the flag the server picks the
     upgrade by (getPreCheckoutPromoProduct), and the only thing separating it
     from the drawer's own add-ons, which are promoItem too.

     Position is carried through the reorder: entryIndex is how a line is
     removed, and a temp cart's entries have no id to fall back on. */
  const entries = (cartInfo.productsAndDetails || [])
    .map((entry, index) => ({ entry, index }))
    .filter(({ entry }) => entry?.product);
  const isUpgrade = ({ entry }) =>
    !!entry.product?.ownPromoData?.isRecurringAddOn;

  [...entries.filter(isUpgrade), ...entries.filter((e) => !isUpgrade(e))].forEach(
    ({ entry, index }) => {
      lines.push(
        productLine(entry, firstMonthPrices, gwpOptionIds, prepaid, index),
      );
    },
  );

  return lines;
};

/* shippingOptions and selectedShippingOptionId are empty until the shipping
   step is wired; with neither, calculateOrderDerivedValues falls back to the
   cart's own firstBoxShipping, which is the server's default for this cart. */
/* What the cart drawer lists. The coupon's free gift is hidden until it is
   chosen at checkout, so anything counting cart lines has to hide it too — the
   nav badge read three over a drawer showing two. */
export const visibleLines = (lines = []) =>
  lines.filter((line) => line.kind !== 'gwp');

export const visibleItemCount = (cartInfo) =>
  cartInfo ? visibleLines(buildLineItems(cartInfo)).length : 0;

/* Legacy's test (containers/review/index.js:91-97), minus the coupon's free
   gift. That gift is attached to a subscription, so it cannot be the only thing
   left in a cart — a checkout showing it alone has nothing to charge for. It is
   the same line the drawer and the nav badge already leave out. */
export const cartIsEmpty = (cartInfo) =>
  !!cartInfo && !cartInfo.subscription && visibleItemCount(cartInfo) === 0;

export const buildTotals = (
  cartInfo,
  {
    shippingOptions = [],
    selectedShippingOptionId = null,
    firstMonthPrices = {},
  } = {},
) => {
  if (!cartInfo) {
    return {
      lines: [],
      subtotal: 0,
      shippingCost: 0,
      shippingLabel: 'FREE',
      discreetShipping: 0,
      protection: 0,
      discount: 0,
      codeDiscount: 0,
      subtotalAfterDiscount: 0,
      tax: 0,
      total: 0,
      itemCount: 0,
      freeShippingGap: 0,
    };
  }

  /* MoneyUtils.calculateTotal parseFloats every subCosts field and sums them, so
     one missing key makes the whole total NaN — which formatAsMoney then reports
     as '0.00', a wrong number rather than an obvious failure. The local temp cart
     is built without shippingInsuranceCost, so fill the gaps before the math. */
  const safeCart = {
    ...cartInfo,
    subCosts: {
      subTotal: 0,
      shipping: 0,
      shippingInsuranceCost: 0,
      couponDiscount: 0,
      pointsDiscount: 0,
      giftCardDiscount: 0,
      taxesAndFees: 0,
      ...cartInfo.subCosts,
    },
  };

  const derived = checkoutUtils.calculateOrderDerivedValues(
    safeCart,
    shippingOptions,
    selectedShippingOptionId,
    safeCart.isBrownBoxSelected,
    null,
    true,
    null,
    false,
    null,
  );

  const { totals } = derived;
  /* calculateOrderDerivedValues folds the discreet shipper into shipping
     (checkoutUtils.js:102-104), as the server does (cart.lib.js:910). Splitting
     it back out gives the summary a row to name it with; the two still sum to
     the one figure legacy shows, and the total is untouched. */
  const discreetShipping = safeCart.isBrownBoxSelected ? DISCREET_SHIPPING : 0;
  const shippingCost = asNumber(totals.shipping) - discreetShipping;
  const lines = buildLineItems(cartInfo, { firstMonthPrices });

  /* subTotal is what the order is before discounts, so a cart whose lines are
     discounted does not add up to it. The three discounts sum the way Start
     Membership sums them (YourCart.js:175-178) — the server's own numbers, not
     recomputed ones.

     Both forms are published because the two screens reconcile differently. The
     checkout summary has a Total to work down to, so it takes the discounts off
     as rows against it. The drawer has no Total — its last number before the
     Checkout button is the subtotal, and a pre-discount one there overstates
     what the shopper is about to pay. */
  /* Coupons and gift cards are both money off from a code, and a shopper
     reading the summary is owed the amount rather than the bookkeeping. Points
     stay their own row: nobody applied them, so the line is what explains where
     they came from.

     A coupon that discounts the plan is left out of the row: subscriptionCostFor
     has already priced that line with it, and the summary has no subtotal to
     take it off, so counting it again shows the same money twice against a
     column that then does not reach the Total. A gift card is never in a line,
     so it stays in either way. */
  const couponDiscount = asNumber(totals.couponDiscount);
  const giftCardDiscount = asNumber(totals.giftCardDiscount);
  const pointsDiscount = asNumber(totals.pointsDiscount);
  const codeDiscount =
    (couponAppliesToPlan(cartInfo) ? 0 : couponDiscount) + giftCardDiscount;
  /* Every discount, still: the drawer works down from the server's pre-discount
     subtotal, where none of them has been applied yet. */
  const discount = couponDiscount + giftCardDiscount + pointsDiscount;
  const subtotal = asNumber(totals.subTotal);

  return {
    lines,
    subtotal,
    discount,
    codeDiscount,
    subtotalAfterDiscount: subtotal - discount,
    shippingCost,
    shippingLabel: shippingCost === 0 ? 'FREE' : `$${shippingCost.toFixed(2)}`,
    discreetShipping,
    protection: asNumber(totals.shippingInsuranceCost),
    tax: asNumber(totals.taxesAndFees),
    couponDiscount: asNumber(totals.couponDiscount),
    pointsDiscount: asNumber(totals.pointsDiscount),
    giftCardDiscount: asNumber(totals.giftCardDiscount),
    total: asNumber(totals.total),
    itemCount: lines.length,
    hasFreeShipping: derived.hasFreeShipping,
    /* What is still owed against the $49 threshold, and zero the moment the
       order has the rate by any route. hasFreeShipping has four of them
       (checkoutUtils.js:142-147) — the threshold, a coupon that grants it, a
       digital-only cart, and a subscription — so the subtotal alone cannot say
       whether there is a gap worth naming. A subscriber told they are $20 short
       of something they already have is the failure this guards against.

       differenceForFreeShipping measures against the discounted amount: it adds
       the coupon and points back on, so a $30 cart with $10 off is $29 short,
       not $19 (checkoutUtils.js:137-141). */
    freeShippingGap: derived.hasFreeShipping
      ? 0
      : Math.max(0, asNumber(derived.differenceForFreeShipping)),
  };
};

export default { buildLineItems, buildTotals, visibleItemCount, visibleLines };
